import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * ContextMenu - Right-click quick action menu for diary event blocks.
 * Portal-based, positioned at cursor with viewport clamping.
 */
function ContextMenu({ x, y, event, onAction, onClose, parkedCount = 0 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ top: y, left: x });

  // Clamp position to viewport after first render
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pad = 8;
    let top = y;
    let left = x;
    if (top + rect.height > window.innerHeight - pad) {
      top = window.innerHeight - rect.height - pad;
    }
    if (left + rect.width > window.innerWidth - pad) {
      left = window.innerWidth - rect.width - pad;
    }
    if (top < pad) top = pad;
    if (left < pad) left = pad;
    setPos({ top, left });
  }, [x, y]);

  // Close on outside click, escape, or scroll
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        e.stopPropagation();
        e.preventDefault();
        // Install a one-shot capture-phase click blocker that survives unmount.
        // When onClose() triggers React setState the component unmounts immediately,
        // so any listener attached here would be cleaned up before the click fires.
        const swallowClick = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          document.removeEventListener('click', swallowClick, true);
        };
        document.addEventListener('click', swallowClick, true);
        // Safety: remove after 400ms in case the click never fires
        setTimeout(() => document.removeEventListener('click', swallowClick, true), 400);
        onClose();
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const handleScroll = () => onClose();

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('keydown', handleEsc);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('keydown', handleEsc);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  const isBooking = event.type === 'booking';
  // Cancelled (3), Completed (4), No Show (6) are not actionable
  const isActionable = isBooking && event.status !== 3 && event.status !== 4 && event.status !== 6;

  const handleAction = (action) => {
    onAction(action, event);
    onClose();
  };

  return ReactDOM.createPortal(
    <div ref={ref} className="dv2-context-menu" style={{ top: pos.top, left: pos.left }}>
      {/* View details - always available */}
      <button className="dv2-context-item" onClick={() => handleAction('view')}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/>
          <path d="M8 5V8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          <circle cx="8" cy="11" r="0.6" fill="currentColor"/>
        </svg>
        View Details
      </button>

      {isActionable && (
        <>
          <button className="dv2-context-item" onClick={() => handleAction('edit')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Edit Booking
          </button>
          <button className="dv2-context-item" onClick={() => handleAction('checkout')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="4" width="12" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M2 7H14" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M5 10H8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            Checkout
          </button>
          <button className="dv2-context-item" onClick={() => handleAction('park')} disabled={parkedCount >= 3}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="2" y="2" width="12" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M8 8V13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              <path d="M5.5 10.5L8 8L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {parkedCount >= 3 ? 'Shelf Full' : 'Park Appointment'}
          </button>
          <div className="dv2-context-divider" />
          <button className="dv2-context-item dv2-context-item--danger" onClick={() => handleAction('cancel')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M6 6L10 10M10 6L6 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            Cancel Booking
          </button>
          <button className="dv2-context-item dv2-context-item--danger" onClick={() => handleAction('noshow')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 13L8 3L13 13H3Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
              <path d="M8 7.5V9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              <circle cx="8" cy="11.2" r="0.6" fill="currentColor"/>
            </svg>
            No Show
          </button>
        </>
      )}

      {/* Events and holidays: edit + delete */}
      {!isBooking && (event.type === 'event' || event.type === 'holiday') && (
        <>
          <button className="dv2-context-item" onClick={() => handleAction('editEvent')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M11.5 2.5L13.5 4.5L5 13H3V11L11.5 2.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Edit {event.type === 'holiday' ? 'Leave' : 'Event'}
          </button>
          <div className="dv2-context-divider" />
          <button className="dv2-context-item dv2-context-item--danger" onClick={() => handleAction('deleteEvent')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 4.5H13M5.5 4.5V3C5.5 2.45 5.95 2 6.5 2H9.5C10.05 2 10.5 2.45 10.5 3V4.5M12 4.5V13C12 13.55 11.55 14 11 14H5C4.45 14 4 13.55 4 13V4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Delete {event.type === 'holiday' ? 'Leave' : 'Event'}
          </button>
        </>
      )}
    </div>,
    document.body
  );
}

export default ContextMenu;
